import { breadcrumbScript } from "@/lib/seo-jsonld";
import { createFileRoute, Link } from "@tanstack/react-router";
import { toast } from "sonner";
import {
  Clock,
  FileText,
  HelpCircle,
  LifeBuoy,
  Mail,
  Megaphone,
  MessageCircle,
  Phone,
  ShieldCheck,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  CONTACT,
  WORK_HOURS,
  adsMailLink,
  mailLink,
  openStatusLabel,
  telLink,
  todayIndex,
  waLink,
} from "@/lib/contact";

export const Route = createFileRoute("/elaqe")({
  head: () => ({
    meta: [
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
      { title: "Əlaqə və dəstək — Albazar.az" },
      {
        name: "description",
        content:
          "Albazar.az dəstək xidməti: bir toxunuşla zəng, WhatsApp, iş saatları, FAQ və reklam üçün əlaqə.",
      },
      { property: "og:title", content: "Əlaqə və dəstək — Albazar.az" },
      {
        property: "og:description",
        content: "Zəng edin və ya WhatsApp vasitəsilə dəstək xidmətinə yazın.",
      },
      { property: "og:url", content: "https://albazar.az/elaqe" },
    ],
    links: [{ rel: "canonical", href: "https://albazar.az/elaqe" }],
    scripts: [
      breadcrumbScript([{ name: "Əlaqə", path: "/elaqe" }]),
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "LocalBusiness",
          name: "Albazar.az",
          url: "https://albazar.az/elaqe",
          telephone: CONTACT.phone,
          email: CONTACT.email,
          image: "https://albazar.az/pwa-icon-512-v3.png",
          address: {
            "@type": "PostalAddress",
            streetAddress: "Nizami küç. 203",
            addressLocality: "Bakı",
            addressCountry: "AZ",
          },
          openingHoursSpecification: [
            {
              "@type": "OpeningHoursSpecification",
              dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"],
              opens: "09:00",
              closes: "22:00",
            },
          ],
        }),
      },
    ],
  }),
  component: ContactPage,
});

function ContactPage() {
  const status = openStatusLabel();
  const today = todayIndex();

  const quick = [
    {
      icon: Phone,
      title: "Bir toxunuşla zəng et",
      desc: CONTACT.phoneLabel,
      href: telLink,
      tone: "bg-primary/10 text-primary",
      external: false,
    },
    {
      icon: MessageCircle,
      title: "WhatsApp ilə yaz",
      desc: "Dərhal cavab alın",
      href: waLink("Salam, Albazar.az dəstəyi ilə əlaqə saxlamaq istəyirəm"),
      tone: "bg-emerald-500/10 text-emerald-600",
      external: true,
    },
    {
      icon: Mail,
      title: "E-poçt göndər",
      desc: CONTACT.email,
      href: mailLink,
      tone: "bg-amber-500/10 text-amber-600",
      external: false,
    },
  ];

  return (
    <div className="container-x max-w-5xl py-8">
      <h1 className="text-3xl font-extrabold tracking-tight md:text-4xl">Əlaqə və dəstək</h1>
      <p className="mt-2 text-sm text-muted-foreground">
        Sualınız var? Bir toxunuşla bizə çatın — zəng, WhatsApp və ya e-poçt.
      </p>

      {/* Sürətli əlaqə */}
      <div className="mt-6 grid gap-3 sm:grid-cols-2">
        {quick.map((q) => (
          <a
            key={q.title}
            href={q.href}
            {...(q.external ? { target: "_blank", rel: "noreferrer" } : {})}
            className="flex items-center gap-3 rounded-2xl border border-border bg-card p-4 shadow-card transition hover:-translate-y-0.5 hover:shadow-lg"
          >
            <span className={`grid size-11 shrink-0 place-items-center rounded-2xl ${q.tone}`}>
              <q.icon className="size-5" />
            </span>
            <span className="min-w-0">
              <span className="block text-sm font-bold">{q.title}</span>
              <span className="block truncate text-xs text-muted-foreground">{q.desc}</span>
            </span>
          </a>
        ))}
      </div>

      <div className="mt-8">
        {/* İş saatları */}
        <section className="rounded-3xl border border-border bg-card p-5 shadow-card">
          <div className="flex items-center justify-between">
            <h2 className="flex items-center gap-2 text-base font-bold">
              <Clock className="size-4 text-primary" /> İş saatları
            </h2>
            <span
              className={`rounded-full px-2.5 py-1 text-[11px] font-bold ${
                status.includes("açıqdır")
                  ? "bg-emerald-500/15 text-emerald-600"
                  : "bg-destructive/10 text-destructive"
              }`}
            >
              {status}
            </span>
          </div>
          <ul className="mt-4 divide-y divide-border text-sm">
            {WORK_HOURS.map((w, i) => (
              <li
                key={w.day}
                className={`flex items-center justify-between py-2 ${
                  i === today ? "font-bold text-primary" : "text-muted-foreground"
                }`}
              >
                <span>{w.day}</span>
                <span>{w.hours}</span>
              </li>
            ))}
          </ul>
        </section>
      </div>

      {/* Dəstək və sənədlər */}
      <div className="mt-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        {[
          {
            icon: HelpCircle,
            title: "Tez-tez verilən suallar",
            desc: "Ən çox soruşulanlar",
            to: "/faq" as const,
          },
          {
            icon: FileText,
            title: "İstifadə qaydaları",
            desc: "Sayt qaydaları və şərtlər",
            to: "/sertler" as const,
          },
          {
            icon: ShieldCheck,
            title: "Məxfilik siyasəti",
            desc: "Məlumatlarınız necə qorunur",
            to: "/mexfilik" as const,
          },
        ].map((c) => (
          <Link
            key={c.title}
            to={c.to}
            className="rounded-2xl border border-border bg-card p-4 shadow-card transition hover:-translate-y-0.5 hover:shadow-lg"
          >
            <c.icon className="size-5 text-primary" />
            <p className="mt-2 text-sm font-bold">{c.title}</p>
            <p className="text-xs text-muted-foreground">{c.desc}</p>
          </Link>
        ))}
        <a
          href={adsMailLink}
          className="rounded-2xl border border-border bg-card p-4 shadow-card transition hover:-translate-y-0.5 hover:shadow-lg"
        >
          <Megaphone className="size-5 text-primary" />
          <p className="mt-2 text-sm font-bold">Reklam yerləşdirmək</p>
          <p className="text-xs text-muted-foreground">{CONTACT.adsEmail}</p>
        </a>
      </div>

      {/* Dəstək xidməti */}
      <section className="mt-6 flex flex-col gap-4 rounded-3xl border border-border bg-card p-5 shadow-card sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-3">
          <span className="grid size-11 place-items-center rounded-2xl bg-primary/10 text-primary">
            <LifeBuoy className="size-5" />
          </span>
          <div>
            <p className="text-sm font-bold">Dəstək xidməti</p>
            <p className="text-xs text-muted-foreground">
              Balans, elan, mağaza və texniki problemlər üzrə kömək
            </p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button asChild className="h-10 rounded-2xl">
            <a href={waLink("Salam, dəstək lazımdır")} target="_blank" rel="noreferrer">
              <MessageCircle className="mr-1 size-4" /> WhatsApp
            </a>
          </Button>
          <Button asChild variant="outline" className="h-10 rounded-2xl">
            <a href={telLink}>
              <Phone className="mr-1 size-4" /> Zəng et
            </a>
          </Button>
        </div>
      </section>

      {/* Müraciət formu */}
      <form
        onSubmit={(e) => {
          e.preventDefault();
          toast.success("Müraciətiniz göndərildi");
          (e.target as HTMLFormElement).reset();
        }}
        className="mt-6 space-y-4 rounded-3xl border border-border bg-card p-6 shadow-card"
      >
        <h2 className="text-base font-bold">Bizə yazın</h2>
        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <Label htmlFor="n">Adınız</Label>
            <Input id="n" required className="mt-2 h-11 rounded-2xl" />
          </div>
          <div>
            <Label htmlFor="e">E-poçt</Label>
            <Input id="e" type="email" required className="mt-2 h-11 rounded-2xl" />
          </div>
        </div>
        <div>
          <Label htmlFor="m">Mesaj</Label>
          <Textarea id="m" rows={5} required className="mt-2 rounded-2xl" />
        </div>
        <Button type="submit" className="h-11 w-full rounded-2xl">
          Göndər
        </Button>
      </form>
    </div>
  );
}
